"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { useLandRisk } from "@/lib/landrisk-context"
import type { UserRole } from "@/lib/types"
import { cn } from "@/lib/utils"
import { ArrowRight, Shield, MapPin, FileCheck } from "lucide-react"

const roles: { id: UserRole; label: string }[] = [
  { id: "buyer", label: "Individual Buyer" },
  { id: "bank-analyst", label: "Bank Analyst" },
  { id: "developer-lawyer", label: "Developer / Lawyer" },
]

export function HeroSection() {
  const { userRole, setUserRole } = useLandRisk()

  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-primary/5 to-background">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwZDk0ODgiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 lg:px-8 lg:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-green-600/20 bg-green-600/10 px-4 py-1.5 text-sm font-medium text-green-700">
            <Shield className="size-4" />
            <span>Trusted Land Risk Intelligence for Kenya</span>
          </div>

          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            <span className="text-primary">Before You Buy,</span>{" "}
            <span>Be Sure</span>
          </h1>

          <p className="mt-6 text-pretty text-lg text-muted-foreground leading-relaxed sm:text-xl">
            Get instant flood risk, legal checks, valuation estimates, and environmental analysis
            for any land parcel in Kenya. Make confident investment decisions backed by data.
          </p>

          <div className="mt-8 flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">I am a:</span>
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setUserRole(role.id)}
                  className={cn(
                    "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                    userRole === role.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  {role.label}
                </button>
              ))}
            </div>

            <Button asChild size="lg" className="mt-2 h-12 gap-2 px-8 text-base">
              <Link href="/identify">
                Start Parcel Risk Check
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="size-4 text-primary" />
              <span>Any location in Kenya</span>
            </div>
            <div className="flex items-center gap-2">
              <FileCheck className="size-4 text-primary" />
              <span>Instant PDF reports</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="size-4 text-primary" />
              <span>5 risk categories</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
